/**
 * Theme — DESIGN.md §13.
 *
 * Three modes: `dark`, `light`, and `system` (follow the OS). Dark is the
 * default, so a first-time visitor with no stored preference and no OS signal
 * gets the Dark Slate/Caviar palette rather than the light one.
 *
 * The resolution rule here is duplicated by the pre-paint script in
 * `index.html`, which runs before React mounts so the first frame is already
 * the right colour. THE TWO MUST AGREE — see the comment there before
 * changing `STORAGE_KEY`, the mode names, or `resolve()`.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export const THEME_STORAGE_KEY = 'slate.theme';

export const THEME_MODES = ['dark', 'light', 'system'] as const;
export type ThemeMode = (typeof THEME_MODES)[number];
/** What is actually painted once `system` has been resolved. */
export type ResolvedTheme = 'dark' | 'light';

const DARK_QUERY = '(prefers-color-scheme: dark)';

/** Matches the `<meta name="theme-color">` swap: caviar vs paper. */
const THEME_COLOR: Record<ResolvedTheme, string> = {
  dark: '#0f141a',
  light: '#ffffff',
};

function isMode(value: unknown): value is ThemeMode {
  return typeof value === 'string' && (THEME_MODES as readonly string[]).includes(value);
}

function readStoredMode(): ThemeMode {
  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (isMode(stored)) return stored;
  } catch {
    // Private browsing and blocked site data both throw on access rather than
    // returning null. Neither is a reason to fail to render.
  }
  return 'dark';
}

function systemPrefersDark(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia(DARK_QUERY).matches;
}

function resolve(mode: ThemeMode): ResolvedTheme {
  if (mode === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return mode;
}

/** Applies the class Tailwind's `darkMode: ['class']` keys off. */
function apply(theme: ResolvedTheme): void {
  const root = document.documentElement;
  root.classList.toggle('dark', theme === 'dark');
  // Native form controls, scrollbars and the caret follow this, not the class.
  root.style.colorScheme = theme;
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOR[theme]);
}

interface ThemeContextValue {
  /** What the user chose — may be `system`. */
  mode: ThemeMode;
  /** What is on screen — never `system`. */
  theme: ResolvedTheme;
  setMode: (mode: ThemeMode) => void;
  /** Steps dark → light → system → dark, for a single-button control. */
  cycleMode: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(readStoredMode);
  const [theme, setTheme] = useState<ResolvedTheme>(() => resolve(readStoredMode()));

  const setMode = useCallback((next: ThemeMode) => {
    setModeState(next);
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // Preference is not persisted; the session still honours the choice.
    }
  }, []);

  const cycleMode = useCallback(() => {
    setModeState((current) => {
      const next = THEME_MODES[(THEME_MODES.indexOf(current) + 1) % THEME_MODES.length] ?? 'dark';
      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, next);
      } catch {
        // As above.
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const next = resolve(mode);
    setTheme(next);
    apply(next);
  }, [mode]);

  // Only `system` cares about the OS flipping mid-session.
  useEffect(() => {
    if (mode !== 'system' || typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia(DARK_QUERY);
    const onChange = () => {
      const next: ResolvedTheme = query.matches ? 'dark' : 'light';
      setTheme(next);
      apply(next);
    };
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [mode]);

  const value = useMemo<ThemeContextValue>(
    () => ({ mode, theme, setMode, cycleMode }),
    [mode, theme, setMode, cycleMode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used inside a ThemeProvider');
  return context;
}
